import defaultPsychologistImage from '@avanta/shared/assets/avatar.svg';
import { Text } from '@avanta/shared/components/Text';
import { Button } from '@avanta/shared/components/ui/Button';
import { Separator } from '@avanta/shared/components/ui/Separator';
import { LOCIZE_NS, MUTATION_KEYS } from '@avanta/shared/constants/common';
import { useIsMutating } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type StepProps, type AppointmentsReserveFormData } from 'pages/AppointmentsReserve/types';
import { areRecordFieldsComplete } from 'pages/AppointmentsReserve/utils';
import { useGetOptions } from '../../../hooks/useGetOptions';
import { StepSection } from '../../ui/StepSection';
import { Documents } from './components/Documents';
import { Observations } from './components/Observations';
import { RecordFields } from './components/RecordFields';
import { SessionSummary } from './components/SessionSummary';

export const ConfirmationStep = ({ handleBack }: StepProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const { watch } = useFormContext<AppointmentsReserveFormData>();
  const { psychologist, contractId, contractRateId, contractAuthorization, recordFields } = watch();
  const { recordFields: recordFieldDefinitions } = useGetOptions();
  const isPendingEdit = useIsMutating({ mutationKey: [MUTATION_KEYS.UPDATE_APPOINTMENT] }) > 0;
  const isPendingBook = useIsMutating({ mutationKey: [MUTATION_KEYS.BOOK_APPOINTMENT] }) > 0;
  const fullName = [psychologist?.name, psychologist?.surname].filter(Boolean).join(' ');
  const isValid =
    Boolean(contractId) &&
    Boolean(contractRateId) &&
    Boolean(contractAuthorization?.trim()) &&
    areRecordFieldsComplete(recordFieldDefinitions, recordFields ?? {});

  return (
    <div className="flex flex-col gap-10 w-216">
      <StepSection title="Detalles de la sesión:">
        <div className="grid overflow-hidden rounded-xl border border-teal-300 grid-cols-[280px_1fr]">
          <SessionSummary />
          <div className="flex min-h-128 flex-col border-teal-300 p-6 border-l border-t-0">
            <div className="flex gap-4">
              <img
                className="size-18 shrink-0 rounded-full object-cover"
                src={psychologist?.imageCircular || defaultPsychologistImage}
                alt={fullName}
              />
              <div className="flex min-w-0 flex-col">
                <Text variant="text-xs-medium" className="text-gray-400">
                  {t('AppointmentsReserve.Steps.confirmation.licensed.label', {
                    professionalNumber: psychologist?.professionalNumber ?? '',
                  })}
                </Text>
                <Text variant="text-md-medium" className="text-denim-800">
                  {fullName}
                </Text>
                <Text variant="text-sm-regular" className="text-denim-800 mt-4 line-clamp-2">
                  {psychologist?.presentations?.es ?? ''}
                </Text>
              </div>
            </div>
            <div className="flex gap-2 text-sm leading-4.5 text-gray-400 mt-10">
              <Info className="size-4 shrink-0 text-teal-600" />
              <Text variant="text-sm-regular" className="text-gray-400">
                {t('AppointmentsReserve.Steps.confirmation.licensed.description')}
              </Text>
            </div>
            <Separator className="my-8" />
            <RecordFields />
          </div>
        </div>
      </StepSection>
      <div className="flex flex-col gap-8 items-end">
        <div className="flex justify-between w-146">
          <Button
            type="button"
            size="lg"
            icon="back"
            variant="terciary"
            className="w-fit"
            onClick={() => handleBack?.()}
          >
            <ChevronLeft className="size-6" />
            {t('Common.back', { ns: LOCIZE_NS.COMMON })}
          </Button>
          <Button
            type="submit"
            size="lg"
            icon="trailing"
            className="min-w-37 w-fit"
            disabled={!isValid}
            isLoading={isPendingEdit || isPendingBook}
          >
            {t('AppointmentsReserve.Steps.confirmation.onSubmit')}
            <ChevronRight className="size-6" />
          </Button>
        </div>
        <Documents />
        <Observations />
      </div>
    </div>
  );
};
